/**
 * Bounded shared transport primitive (APP-W01b, replacing the W01a inline
 * implementation with an equivalent, tested base).
 *
 * Characteristics (W01b commitments):
 * - caller abort composes INTO the internal controller (one abort seam; the
 *   external signal never races the internal timer)
 * - generation invalidation composes through the same external signal (an
 *   identity transition aborts the whole in-flight unit)
 * - ONE logical deadline covers response framing AND the body read
 * - headers pass through Headers semantics exactly once and are normalized
 *   to a plain record, so a replay could resend identical header bytes
 * - bodyless requests keep their real method/headers (no invented
 *   Content-Type on a GET/probe)
 * - every failure is an outcome; this layer never throws and never retries
 *
 * The qualified `apiFetch` (CSRF attach/replay semantics) will compose on
 * this base inside api.ts; the raw identity probe keeps its dedicated
 * `rawFetch` seam with its tighter 10-second total deadline.
 */

export interface ComposedRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string | null;
  signal?: AbortSignal;
  /** Total logical deadline (ms) through response AND body read. Default 30s. */
  deadlineMs?: number;
}

export type TransportOutcome =
  | { kind: 'response'; status: number; ok: boolean; bodyText: string }
  | { kind: 'timeout' }
  | { kind: 'network-error' }
  | { kind: 'aborted' };

/** JSON API total budget: 30 seconds through response AND body. */
export const API_TOTAL_DEADLINE_MS = 30_000;
/** Auth/CSRF ceiling — bounded INSIDE any remaining enclosing budget. */
export const AUTH_BUDGET_CEILING_MS = 10_000;
/** Identity-probe total deadline (W01a kept; auth minors abuse no more). */
export const IDENTITY_PROBE_TIMEOUT_MS = 10_000;

/**
 * The bounded deadline for an auth/CSRF request: never larger than the
 * remaining enclosing budget, never larger than the 10-second auth ceiling.
 */
export function boundedAuthDeadline(remainingMs: number = API_TOTAL_DEADLINE_MS): number {
  return Math.max(0, Math.min(AUTH_BUDGET_CEILING_MS, remainingMs));
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

/**
 * The bounded transport. Never throws; every outcome is structured.
 * The caller's abort signal (which may itself be a composed
 * identity-generation signal) is bridged onto the internal controller so
 * timeout, caller-abort and identity-generation abort all funnel through
 * ONE AbortController race — there is no second abort mechanism.
 */
export async function boundedFetch(
  url: string,
  options: ComposedRequestOptions = {}
): Promise<TransportOutcome> {
  const timeoutMs = options.deadlineMs ?? API_TOTAL_DEADLINE_MS;

  // Headers through Headers semantics (joint as record inputs already are).
  const headers = options.headers == null ? undefined : new Headers(options.headers);

  const controller = new AbortController();
  let externalAbort = false;

  const forwardExternalAbort = () => {
    externalAbort = true;
    controller.abort();
  };
  if (options.signal) {
    if (options.signal.aborted) {
      // Fail closed BEFORE any wire call: an already-invalidated caller
      // (unmount, stale identity generation) starts nothing.
      return { kind: 'aborted' };
    }
    options.signal.addEventListener('abort', forwardExternalAbort);
  }

  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'include',
      ...(headers ? { headers } : {}),
      ...(options.body != null ? { body: options.body } : {}),
      signal: controller.signal,
    });
    // Body read stays under the same ONE logical deadline: the timer keeps
    // running until it fires, and an aborted signal rejects the pending
    // body read, so nothing can exceed the enclosing budget.
    const bodyText = await response.text();
    return { kind: 'response', status: response.status, ok: response.ok, bodyText };
  } catch (error) {
    if (isAbortError(error)) {
      return externalAbort ? { kind: 'aborted' } : { kind: 'timeout' };
    }
    return { kind: 'network-error' };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', forwardExternalAbort);
  }
}

/**
 * Dual-timeout helper: an auth/CSRF call runs inside the local bounded
 * ceiling and may not exceed the remaining enclosing budget either. The
 * caller passes its REMAINING enclosing budget; the effective deadline is
 * `min(boundedAuthDeadline(remaining), remaining)`.
 */
export function authDeadlineWithin(remainingMs: number): number {
  return boundedAuthDeadline(remainingMs);
}

/**
 * Raw fetch with deadline-through-body (W01a probe seam, unchanged public
 * behaviour). Timeout is signalled as the `timeout` outcome; an
 * external/superseding abort as `aborted`; any other failure (DNS, CORS,
 * TypeError) as `network-error`.
 */
export async function rawFetch(url: string, options: RawFetchOptions = {}): Promise<RawTransportOutcome> {
  return boundedFetch(url, {
    method: options.method,
    headers: options.headers,
    signal: options.signal,
    deadlineMs: options.timeoutMs ?? IDENTITY_PROBE_TIMEOUT_MS,
  });
}

export interface RawFetchOptions {
  method?: string;
  headers?: Record<string, string>;
  /** External abort signal (e.g. caller-owned controller for unmount). */
  signal?: AbortSignal;
  /** Total deadline in milliseconds, through response body. Default 10s. */
  timeoutMs?: number;
}

export type RawTransportOutcome = TransportOutcome;
